import {
  PORTFOLIO_CATEGORY_NAME_MAX_LENGTH,
  type PortfolioCategoryAdmin,
} from '@repo/api-contracts';
import { GripVertical } from 'lucide-react';
import { type ButtonHTMLAttributes, type HTMLAttributes, useState } from 'react';
import { Button, DropdownMenu, Input } from '@/shared/ui';
import { useDeletePortfolioCategory, useRenamePortfolioCategory } from '../model';

export const PortfolioCategoryRow = ({
  category,
  first,
  last,
  disabled,
  canMove,
  onMove,
  handleProps,
  rowProps,
  dropEdge,
}: {
  category: PortfolioCategoryAdmin;
  first: boolean;
  last: boolean;
  disabled: boolean;
  canMove: boolean;
  onMove: (direction: 'UP' | 'DOWN') => void;
  handleProps: ButtonHTMLAttributes<HTMLButtonElement>;
  rowProps: HTMLAttributes<HTMLLIElement>;
  dropEdge?: 'before' | 'after';
}) => {
  const rename = useRenamePortfolioCategory();
  const deletion = useDeletePortfolioCategory();
  const [name, setName] = useState(category.name);
  const [editing, setEditing] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const busy = disabled || rename.isPending || deletion.isPending;
  const save = async () => {
    if (busy || !name.trim()) return;
    deletion.reset();
    setMessage(null);
    if (await rename.renameCategory({ id: category.id, name: name.trim() })) {
      setEditing(false);
      setMessage('이름을 저장했습니다.');
    }
  };
  const remove = async () => {
    if (busy || !window.confirm(`"${category.name}" 카테고리를 삭제하시겠습니까?`)) return;
    rename.reset();
    setMessage(null);
    if (await deletion.deleteCategory({ id: category.id })) setDeleted(true);
  };
  if (deleted) return null;
  return (
    <li {...rowProps} className="relative rounded-2xl border bg-background px-3 py-2">
      {dropEdge ? (
        <span
          className={`pointer-events-none absolute inset-y-0 z-10 border-l-2 border-primary text-xs text-primary ${dropEdge === 'before' ? '-left-1' : '-right-1'}`}
        >
          여기에 놓기
        </span>
      ) : null}
      <div className="flex items-center gap-1">
        <button
          {...handleProps}
          type="button"
          className="cursor-grab rounded p-1 text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 active:cursor-grabbing"
          aria-label={`${category.name} 순서 이동`}
          title="끌어서 순서 변경 · 방향키로 이동"
          disabled={busy || !canMove}
          draggable={!busy && canMove && handleProps.draggable}
          onKeyDown={(event) => {
            if (busy || !canMove) return;
            if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && !first) {
              event.preventDefault();
              onMove('UP');
            }
            if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && !last) {
              event.preventDefault();
              onMove('DOWN');
            }
          }}
        >
          <GripVertical size={14} aria-hidden="true" />
        </button>
        {editing ? (
          <form
            className="flex items-center gap-1"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <Input
              aria-label={`${category.name} 이름`}
              value={name}
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
              className="h-8 w-40"
              autoFocus
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              aria-label={`${category.name} 이름 저장`}
              disabled={
                busy ||
                !name.trim() ||
                Array.from(name.trim()).length > PORTFOLIO_CATEGORY_NAME_MAX_LENGTH
              }
            >
              저장
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setName(category.name);
                rename.reset();
              }}
            >
              취소
            </Button>
          </form>
        ) : (
          <>
            <span className="text-sm" title={`${category.linkedPortfolioCount}개 사례에서 사용 중`}>
              {category.name}
            </span>
            <fieldset disabled={busy} className="contents disabled:opacity-50">
              <DropdownMenu
                ariaLabel={`${category.name} 메뉴`}
                items={[
                  {
                    label: '이름 수정',
                    onSelect: () => {
                      if (!busy) {
                        setName(category.name);
                        setEditing(true);
                      }
                    },
                  },
                  {
                    label: '삭제',
                    isDestructive: true,
                    onSelect: () => {
                      if (!busy) void remove();
                    },
                  },
                ]}
              />
            </fieldset>
          </>
        )}
      </div>
      {rename.error || deletion.error ? (
        <p role="alert" className="text-sm text-destructive">
          {rename.error ?? deletion.error}
        </p>
      ) : null}
      {message ? (
        <p role="status" className="text-sm">
          {message}
        </p>
      ) : null}
    </li>
  );
};
